<template>
	<div class="selectable-list-wrapper">
		<div class="selectable-list-container">
			<div class="flex items-center gap-2">
				<MultiSelect
					v-model="tempSelection"
					:options="props.options"
					:option-label="optionLabel"
					:option-value="optionValue"
					:placeholder="placeholder"
					:filter="filter"
					@update:modelValue="onSelect"
					class="w-full custom-multiselect"
					:show-clear="false"
					display="chip"
					:maxSelectedLabels="0"
					selectedItemsLabel="{0} items selected"
				/>
				<div class="clear-all-button" @click="clearAll" title="Clear All">
					<FontAwesomeIcon :icon="['far', 'xmark']" />
				</div>
			</div>

			<div v-if="selectedItems.length > 0" class="selected-items-section">
				<div class="selected-items-list">
					<div v-for="item in selectedItems" :key="getItemValue(item)" class="chip-wrapper">
						<Chip :label="getItemLabel(item)" class="chip-content" />
						<div class="remove-button" @click="removeItem(item)" title="Remove">
							<FontAwesomeIcon :icon="['far', 'xmark']" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue"
import MultiSelect from "primevue/multiselect"
import Chip from "primevue/chip"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faXmark } from "@fortawesome/pro-regular-svg-icons"

library.add(faXmark)

interface Props {
	modelValue?: any[]
	options: any[]
	optionLabel?: string
	optionValue?: string
	placeholder?: string
	filter?: boolean
	preserveOptionOrder?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: () => [],
	placeholder: "Select an Item",
	filter: true,
	preserveOptionOrder: false,
})

const emit = defineEmits<{
	"update:modelValue": [value: any[]]
}>()

const tempSelection = ref<any[]>([])

// Sync tempSelection with modelValue
watch(
	() => props.modelValue,
	(newValue) => {
		// Filtra eventuali valori undefined o null
		const validValues = (newValue || []).filter((v) => v !== undefined && v !== null)
		tempSelection.value = [...validValues]
	},
	{ immediate: true }
)

const getItemLabel = (item: any): string => {
	if (!item) return ""
	return props.optionLabel ? item[props.optionLabel] : item
}

const getItemValue = (item: any): any => {
	if (!item) return item
	return props.optionValue ? item[props.optionValue] : item
}

// Already selected items (complete objects)
const selectedItems = computed(() => {
	if (!props.modelValue || props.modelValue.length === 0) return []

	const filtered = props.options.filter((option) => {
		const optionVal = getItemValue(option)
		return props.modelValue.includes(optionVal)
	})

	if (props.preserveOptionOrder) return filtered

	return filtered.sort((a, b) => {
		const labelA = getItemLabel(a).toLowerCase()
		const labelB = getItemLabel(b).toLowerCase()
		return labelA.localeCompare(labelB)
	})
})

const onSelect = (newValues: any[]) => {
	// Update the modelValue directly with the new values
	emit("update:modelValue", newValues)
}

const removeItem = (item: any) => {
	const itemValue = getItemValue(item)
	const currentValue = props.modelValue || []
	emit(
		"update:modelValue",
		currentValue.filter((v) => v !== itemValue)
	)
}

const clearAll = () => {
	emit("update:modelValue", [])
}
</script>

<style scoped>
.selectable-list-wrapper {
	border: 2px solid var(--p-surface-300);
	border-radius: var(--p-border-radius-md);
	padding: 1rem;
	background-color: #fff;
}

.selectable-list-container {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	width: 100%;
}

.selected-items-section {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.list-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-weight: 600;
	font-size: 1rem;
	color: var(--text-color);
	padding-bottom: 0.5rem;
	border-bottom: 2px solid var(--surface-300);
}

.clear-all-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: #000;
	color: white;
	cursor: pointer;
	transition: all 0.2s;
	flex-shrink: 0;
}

.clear-all-button:hover {
	background-color: #333;
	transform: scale(1.1);
}

.clear-all-button svg {
	font-size: 0.75rem;
}

.selected-items-list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	max-height: 300px;
	overflow-y: auto;
	padding: 0.5rem;
	background-color: var(--surface-50);
	border: 1px solid var(--surface-200);
	border-radius: 6px;
}

.chip-wrapper {
	position: relative;
	display: flex;
	align-items: center;
}

.chip-content {
	flex: 1;
	padding-right: 2.5rem;
}

.remove-button {
	position: absolute;
	right: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: #000;
	color: white;
	cursor: pointer;
	transition: all 0.2s;
	flex-shrink: 0;
	z-index: 1;
}

.remove-button:hover {
	background-color: #333;
	transform: scale(1.1);
}

.remove-button svg {
	font-size: 0.75rem;
}

/* Scrollbar styling - sempre visibile */
.selected-items-list {
	scrollbar-width: thin;
	scrollbar-color: var(--surface-400) var(--surface-100);
}

.selected-items-list::-webkit-scrollbar {
	width: 8px;
}

.selected-items-list::-webkit-scrollbar-track {
	background: var(--surface-100);
	border-radius: 4px;
}

.selected-items-list::-webkit-scrollbar-thumb {
	background: var(--surface-400);
	border-radius: 4px;
	border: 2px solid var(--surface-100);
}

.selected-items-list::-webkit-scrollbar-thumb:hover {
	background: var(--surface-500);
}
</style>
